import { stripURLQueryParams } from './urlUtilities';

export interface ParamPairSet {
	[index: string]: ParamPair;
}

export interface ParamPair {
	name: string;
	value?: string[];
}

export default class ParamPairs {
	public pairs: ParamPairSet;

	constructor(url: string = window.location.search) {
		this.pairs = this.buildPairs(url);
	}

	get length(): number {
		let count: number = 0;

		for (const key in this.pairs) {
			if (this.pairs.hasOwnProperty(key)) {
				count++;
			}
		}

		return count;
	}

	get keyCount(): number {
		return this.length;
	}

	get valueCount(): number {
		let count: number = 0;

		for (const key in this.pairs) {
			if (this.pairs.hasOwnProperty(key)) {
				count += this.pairs[key].value.length;
			}
		}

		return count;
	}

	public buildPairs(url: string): ParamPairSet {
		if (!url)
			return {};

		const params: string[] = decodeURIComponent(url).split('?')[1].split('&');

		const holder: any = {};
		for (let i: number = 0; i < params.length; i++) {
			const pair: string[] = params[i].split('=');
			let val: string[] = holder[pair[0]];
			if (val === undefined) {
				holder[pair[0]] = {
					name: pair[0],
					value: []
				};
			}
			holder[pair[0]].value.push(pair[1]);
		}

		return holder;
	}

	public add(name: string, value: string): void {
		if (this.pairs.hasOwnProperty(name)) {
			if (this.pairs[name].value.indexOf(value) === -1) {
				this.pairs[name].value.push(value);
			}
		} else {
			this.pairs[name] = {
				name: name,
				value: [value]
			};
		}
	}
	public removeValue(name: string, value: string): void {
		if (this.pairs.hasOwnProperty(name)) {
			const valueIndex: number = this.pairs[name].value.indexOf(value);
			if (valueIndex > -1) {
				this.pairs[name].value.splice(valueIndex, 1);
			}
			if (!this.pairs[name].value.length) {
				delete this.pairs[name];
			}
		}
	}

	public removePair(name: string): void {
		if (this.pairs.hasOwnProperty(name)) {
			delete this.pairs[name];
		}
	}

	public update(name: string, value: string): void {
		if (this.pairs[name]) {
			this.pairs[name].value = [value];
		} else {
			// NOTE: Should it instead error? -SB 2017-12-9
			this.add(name, value);
		}
	}

	public destroy(): void {
		for (let key in this.pairs) {
			if (this.pairs.hasOwnProperty(key)) {
				delete this.pairs[key];
			}
		}
	}

	public destroyCB: EventListener = (event: Event) => {
		this.destroy();
	}

	public buildQueryString(baseUrl: string = window.location.href): string {
		let paramString: string = '';

		for (let key in this.pairs) {
			if (this.pairs.hasOwnProperty(key)) {
				let element: ParamPair = this.pairs[key];
				if (element.value) {
					for (let i: number = 0; i < element.value.length; i++) {
						const myKey: string = encodeURIComponent(element.name);
						const myValue: string = encodeURIComponent(element.value[i]);
						if (paramString.length > 1) {
							paramString += '&';
						}
						paramString += `${myKey}=${myValue}`;
					}
				}
			}
		}

		if (paramString.length) {
			paramString = `?${paramString}`;
		}

		return stripURLQueryParams(baseUrl) + paramString;
	}

	public contains(key: string, value?: string): boolean {
		if (!this.pairs.hasOwnProperty(key)) {
			return false;
		} else if (!value || (this.pairs[key].value.indexOf(value) === -1)) {
			return false;
		} else {
			return true;
		}
	}

	// TODO: Is there a better way to do the arguments?
	/**
	 * Count the number of keys that exist with filter applied
	 * @param {ParamPair[]} excludedPairs Values/keys to exclude
	 * @param {ParamPairSet} pairSet The set to count
	 * @return {number} The number of values
	 */
	public filteredKeyCount({excludedPairs, pairSet = this.pairs}: {excludedPairs?: ParamPair[], pairSet?: ParamPairSet}): number {
		let count: number = 0;

		if (!excludedPairs) {
			for (let key in pairSet) {
				if (pairSet.hasOwnProperty(key)) {
					count++;
				}
			}

			return count;
		} else {
			let tempSet: ParamPairSet = this.pairs;
			for (let i: number = 0; i < excludedPairs.length; i++) {
				const exPair: ParamPair = excludedPairs[i];
				if (tempSet.hasOwnProperty(exPair.name)) {
					delete tempSet[exPair.name];
				}
			}
			return this.filteredKeyCount({pairSet: tempSet});
		}
	}

	/**
	 * Count the number of values that exist
	 * @param {ParamPair[]} excludedPairs Values/keys to exclude
	 * @param {ParamPairSet} pairSet The set to count
	 * @return {number} The number of values
	 */
	public filteredValueCount({excludedPairs, pairSet = this.pairs}: {excludedPairs?: ParamPair[], pairSet?: ParamPairSet}): number {
		let count: number = 0;

		if (!excludedPairs) {
			for (let key in pairSet) {
				if (pairSet.hasOwnProperty(key)) {
					const pair: ParamPair = pairSet[key];
					count += pair.value.length;
				}
			}

			return count;
		} else {
			let tempSet: ParamPairSet = this.pairs;
			for (let i: number = 0; i < excludedPairs.length; i++) {
				const exPair: ParamPair = excludedPairs[i];
				if (tempSet.hasOwnProperty(exPair.name)) {
					if (exPair.hasOwnProperty('value') && (exPair.value.length)) {
						for (let i: number = 0; i < exPair.value.length; i++) {
							const value: string = exPair.value[i];
							const valueIndex: number = tempSet[exPair.name].value.indexOf(value);
							if (valueIndex !== -1) {
								tempSet[exPair.name].value.splice(valueIndex, 1);
							}
						}
					} else {
						delete tempSet[exPair.name];
					}
				}
			}
			return this.filteredValueCount({pairSet: tempSet});
		}
	}
}